import { Box, GridItem, useBreakpointValue } from '@chakra-ui/react';
import { ColumnNavLayout } from '@time-line/components/common';
import type { BannerCardVariant } from 'components/common/BannerCard';
import { BannerCard } from 'components/common/BannerCard';
import { PageModuleResolver } from 'components/common/PageModuleResolver';
import { PageWrapper } from 'components/common/PageWrapper';
import { Seo } from 'components/common/Seo';
import { ProductCard } from 'components/product/ProductCard';
import { FeaturedProduct } from 'components/shop/FeaturedProduct';
import type { ShopSlug } from 'hooks/pages/useProductListingPage';
import { useProductListingPage } from 'hooks/pages/useProductListingPage';
import { useProductCollection } from 'hooks/product/useProductCollection';
import { useAnalytics } from 'hooks/useAnalytics';
import { useGlobals } from 'hooks/useGlobals';
import { useIsSkincare } from 'hooks/useIsSkincare';
import { usePrefetchNavigation } from 'hooks/usePrefetchNavigation';
import { env } from 'lib/constants/env';
import { useRouter } from 'next/router';

interface ShopProps {
  slug: ShopSlug;
}

const PRODUCT_LIST_IDS = {
  PRIMARY: 'Primary',
  SECONDARY: 'Secondary',
};

const mainContainerProps = {
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridColumn: {
    base: '1 / -1',
    md: '5 / -1',
    lg: '4 / -1',
  },
};

export function Shop({ slug }: ShopProps) {
  const { analytics } = useAnalytics();

  const { data: productListing } = useProductListingPage(slug);
  const { data: globals } = useGlobals();
  const router = useRouter();
  const isSkincare = useIsSkincare();

  const {
    seo,
    title,
    primaryCollection,
    featuredProduct,
    isMarqueeVisible,
    blocks,
  } = productListing;

  const primaryProducts = useProductCollection(primaryCollection);
  const hasPrimaryProducts = primaryProducts.length > 0;

  const primaryNavItems = globals?.siteSettings.plp;
  const secondaryNavItems = globals?.siteSettings.secondaryPlpMenu;
  usePrefetchNavigation(primaryNavItems, secondaryNavItems);

  const isBannerCardVisible =
    env.NEXT_PUBLIC_SKINCARE_CARDS_ENABLED && !isSkincare;

  const bannerCardVariant: BannerCardVariant =
    primaryProducts.length % 3 === 0 ? 'wide' : 'default';

  analytics.viewItemList({
    listId: PRODUCT_LIST_IDS.PRIMARY,
    products: primaryProducts.map((product) => product.title),
  });

  const bannerCardBpVariant = useBreakpointValue<BannerCardVariant>(
    {
      base: 'default',
      lg: bannerCardVariant,
    },
    { fallback: 'base' },
  );

  return (
    <PageWrapper>
      <Seo seo={seo} />
      <ColumnNavLayout
        title={title}
        primary={primaryNavItems}
        secondary={secondaryNavItems}
        isMarqueeVisible={isMarqueeVisible}
        isDividerVisible
        currentPath={router.asPath}
      >
        {featuredProduct && (
          <Box
            {...mainContainerProps}
            gridTemplateRows={{ base: 'auto 1fr', lg: 'auto' }}
            gridRowGap="sm"
            mb={hasPrimaryProducts ? { base: 'lg', md: '2xl' } : 0}
          >
            <FeaturedProduct {...featuredProduct} />
          </Box>
        )}
        {hasPrimaryProducts && (
          <Box {...mainContainerProps} gridRowGap={{ base: 'lg', md: '2xl' }}>
            {primaryProducts.map((product, i) => (
              <GridItem key={i} colSpan={{ base: 6, md: 4, lg: 3 }}>
                <ProductCard
                  {...product}
                  index={i}
                  collectionName={PRODUCT_LIST_IDS.PRIMARY}
                  isDoctorRecommended={false}
                  isATCVisible
                />
              </GridItem>
            ))}
            {isBannerCardVisible && (
              <GridItem
                key="banner-card"
                colSpan={
                  bannerCardBpVariant === 'wide'
                    ? { base: 6, md: 8, lg: 9 }
                    : { base: 12, md: 4, lg: 3 }
                }
              >
                <BannerCard variant={bannerCardBpVariant} />
              </GridItem>
            )}
          </Box>
        )}
      </ColumnNavLayout>

      {blocks && <PageModuleResolver blocks={blocks} />}
    </PageWrapper>
  );
}
